import { useState } from 'react';
import { IconHistory, IconInfoCircle, IconKey, IconLayoutSidebarRight, IconListDetails, IconPlus, IconUser } from '@tabler/icons-react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { HeaderTool, Page, PageHeader, useShell } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import { kitAccess } from '../access';
import { TabTable } from './TabTable';
import { ADM, PARAMS, PINS, TABS, viewPath, type Tab } from './data';
import s from './admin.module.css';
import '../i18n';

const OWNERS = ['KTT', 'BOD', 'HR', 'Trưởng CS', 'Trưởng TRF', 'Admin IT'].join(' · '); // tên vai trò = dữ liệu, không dịch

// Mint-Admin · trang gốc HANA Kit (/kit): không có nút ← (canvas dbab397 "Admin bỏ ←"). Chỉ ADMIN; ghế khác → noperm của board.
// Trang khung theo board: cột mục quản trị + Trung tâm tham số (app_param). Màn đầy đủ do luồng H chuyển từ Ezy sang (02/Nov).

const TAB_KEYS = TABS.map(([k]) => k);
const initials = (n: string) => n.slice(0, 2).toUpperCase();

/** Đi tới đường dẫn trong Kit (có thể kèm `?tab=`). */
function useGo() {
  const nav = useNavigate();
  return (path: string) => {
    const [to, q] = path.split('?');
    void nav({ to: to!, search: q ? Object.fromEntries(new URLSearchParams(q)) : {} } as never);
  };
}

/** Đầu trang dùng chung cho mọi biến thể (kể cả noperm, như board). */
function AdminHead({ seat }: { seat: string }) {
  const t = useT();
  const go = useGo();
  const toast = useToast();
  return (
    <PageHeader
      title={t('kit.adminTitle')}
      meta={
        <span className={s.meta}>
          <span className={s.seat}>{seat}</span>
          <span role="img" className={s.info} aria-label={t('kit.adminInfo')} title={t('kit.adminInfo')}>
            <IconInfoCircle size={16} aria-hidden="true" />
          </span>
        </span>
      }
      noFind
      actions={
        <>
          <input className={s.find} aria-label={t('kit.find')} placeholder={t('kit.find')} />
          <Button icon={<IconListDetails size={16} aria-hidden="true" />} onClick={() => go('/kit/queue')}>
            {t('kit.queue')} 4
          </Button>
          <HeaderTool label={t('kit.rightPanel')} icon={IconLayoutSidebarRight} onClick={() => toast({ msg: t('kit.later') })} />
        </>
      }
    />
  );
}

export function AdminHome({ view = 'params' }: { view?: string }) {
  const t = useT();
  const go = useGo();
  const toast = useToast();
  const { admin } = useShell();
  const search = useRouterState({ select: (r) => r.location.searchStr });
  const acc = kitAccess(search, admin);
  const qTab = new URLSearchParams(search).get('tab') as Tab | null;
  const tab: Tab = qTab && TAB_KEYS.includes(qTab) ? qTab : 'param';
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const seat = acc.admin || !acc.viewer ? `ADMIN · ${acc.viewer ?? 'DAVID'}` : acc.viewer;
  const say = () => toast({ msg: t('kit.later') });
  const cur = view === 'params' ? `params:${tab}` : view;

  if (acc.state === 'noperm') {
    return (
      <Page kind="table">
        <AdminHead seat={seat} />
        <section className={s.main} aria-label={t('kit.noPermTitle')} style={{ minHeight: 'calc(100dvh - var(--page-head-h, 82px) - 18px)' }}>
          <div className={s.center}>
            <StateView
              state="noperm"
              title={t('kit.noPermTitle')}
              desc={t('kit.noPermDesc')}
              action={<Button variant="primary" icon={<IconKey size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('kit.askSent') })}>{t('kit.askAccess')}</Button>}
            />
          </div>
        </section>
      </Page>
    );
  }

  const params = view === 'params';
  return (
    <Page kind="table">
      <AdminHead seat={seat} />
      <div className={s.split}>
        <nav className={s.side} aria-label={t('kit.groups')}>
          <div className={s.pins}>
            {PINS.map((p) => (
              <button key={p.key} type="button" className={s.pin} aria-current={(p.key === 'params' ? params : view === p.key) ? 'page' : undefined} onClick={() => p.to && go(p.to)}>
                <span className={s.pinK} title={p.label}>{p.label}</span>
                {p.n && <span className={s.pinN}>{p.n}</span>}
              </button>
            ))}
          </div>
          <div className={s.groups}>
            {ADM.map(([g, items]) => {
              const closed = !!shut[g];
              return (
                <div key={g}>
                  <button type="button" className={s.gh} aria-expanded={!closed} onClick={() => setShut((o) => ({ ...o, [g]: !closed }))}>
                    <span className={s.ghC} aria-hidden="true">{closed ? '▸' : '▾'}</span>
                    <span className={s.ghK}>{g}</span>
                    <span className={s.cnt}>{items.length}</span>
                  </button>
                  {!closed &&
                    items.map((i) => (
                      <button key={i.view + i.label} type="button" className={s.it} title={t('kit.itemOwner', { l: i.label, o: i.owner })} aria-current={cur === i.view ? 'page' : undefined} onClick={() => go(viewPath(i.view))}>
                        <span className={s.itK}>{i.label}</span>
                      </button>
                    ))}
                </div>
              );
            })}
          </div>
        </nav>
        <section className={s.main} aria-label={params ? t('kit.params') : t('kit.moving')}>
          {params ? (
            <>
              <div className={s.vhead}>
                <div className={s.vrow}>
                  <div className={s.vt}>
                    <h2>{t('kit.params')}</h2>
                    <span className={s.owner} title={t('kit.owner', { o: OWNERS })}>
                      <IconUser size={12} stroke={2.2} aria-hidden="true" />
                      {OWNERS}
                    </span>
                    <span role="img" className={s.info} aria-label={t('kit.paramsInfo')} title={t('kit.paramsInfo')}>
                      <IconInfoCircle size={16} aria-hidden="true" />
                    </span>
                  </div>
                  <div className={s.acts}>
                    <Button icon={<IconHistory size={16} aria-hidden="true" />} onClick={say}>{t('kit.history')}</Button>
                    <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={say}>{t('kit.addParam')}</Button>
                  </div>
                </div>
                <div role="tablist" aria-label={t('kit.tabs')} className={s.tabs}>
                  {TABS.map(([k, l, n]) => (
                    <button key={k} type="button" role="tab" aria-selected={tab === k} className={s.tab} onClick={() => go(k === 'param' ? '/kit' : `/kit?tab=${k}`)}>
                      {l}
                      {n !== undefined && <span className={s.tabN}>{n}</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div className={s.body}>
                <StateView
                  state={acc.state}
                  title={acc.state === 'error' ? t('kit.errTitle') : t('kit.emptyTitle')}
                  action={acc.state === 'error' ? <Button onClick={() => location.reload()}>{t('common.retry')}</Button> : undefined}
                  skeleton={<Skeleton rows={8} />}
                >
                  {tab !== 'param' ? <TabTable tab={tab} /> : (
                  <div role="table" aria-label="app_param" className={s.tbl}>
                    <div role="row" className={`${s.tr} ${s.th}`}>
                      {['Key', t('kit.thValue'), t('kit.thUnit'), t('kit.thOwner'), t('kit.thSince'), t('kit.thLast')].map((h, i) => (
                        <span key={h} role="columnheader" className={i === 1 ? s.r : undefined}>{h}</span>
                      ))}
                    </div>
                    {PARAMS.map((p) => {
                      const who = p.last.split(' · ')[0]!;
                      return (
                        <div key={p.key} role="row" className={s.tr} title={p.desc}>
                          <span role="cell" className={s.k}>{p.fin ? '★ ' : ''}{p.key}</span>
                          <span role="cell" className={`${s.k} ${s.r}`}>{p.value}</span>
                          <span role="cell">{p.unit}</span>
                          <span role="cell"><span className={`${s.cipt} ${p.fin ? s.warn : ''}`}>{p.owner}</span></span>
                          <span role="cell">{p.next ? <span className={`${s.cipt} ${s.pri}`}>→ {p.next} · 01/Nov</span> : p.since}</span>
                          <span role="cell" className={s.who}><span className={s.av} aria-hidden="true">{initials(who)}</span>{who}</span>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </StateView>
              </div>
            </>
          ) : (
            <div className={s.center}>
              <StateView state="empty" title={t('kit.moving')} />
            </div>
          )}
        </section>
      </div>
    </Page>
  );
}
